import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Bookmark, ChevronLeft, ChevronRight, RotateCcw, Target, TrendingDown, ArrowRight, ClipboardList } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { testCategories, questionBank, targetScores } from "@/data/tests";
import { scoreTest } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { ProgressRing, ProgressBar } from "@/components/ProgressRing";
import { SectionTitle } from "@/components/common";

export default function TestPage() {  const { data, updateData } = useApp();
  const [cat, setCat] = useState(null);
  const [phase, setPhase] = useState("select"); // select | run | result

  const start = (c) => { setCat(c); setPhase("run"); };
  const finish = (attempt) => {
    updateData((prev) => ({ ...prev, testAttempts: [...prev.testAttempts, attempt] }));
    setPhase("result");
  };

  if (phase === "run") return <Runner cat={cat} onFinish={finish} onExit={() => setPhase("select")} />;
  if (phase === "result") {
    const attempt = data.testAttempts[data.testAttempts.length - 1];
    return <Result attempt={attempt} onRetry={() => setPhase("run")} onBack={() => setPhase("select")} />;
  }

  return (
    <div className="space-y-6">
      <SectionTitle title="Simulasi Test" desc="Latihan. Diagnosa. Tingkatkan." />
      <div className="grid gap-5 sm:grid-cols-3">
        {testCategories.map((c) => (
          <div key={c.id} className="flex flex-col rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#F3E8FF] to-[#EDE9FE] text-[#7C3AED]"><ClipboardList className="h-5 w-5" /></span>
            <h3 className="mt-3 font-display text-base font-bold text-slate-900">{c.label}</h3>
            <p className="mt-1 text-sm text-slate-500">{c.desc}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {c.sections.map((sec) => <span key={sec} className="rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-[#6D28D9]">{sec}</span>)}
            </div>
            <div className="mt-auto pt-4">
              <Button onClick={() => start(c.id)} data-testid={`start-test-${c.id}`} className="w-full rounded-full bg-[#7C3AED] hover:bg-[#6D28D9]">Mulai ({questionBank[c.id].length} soal)</Button>
            </div>
          </div>
        ))}
      </div>

      {data.testAttempts.length > 0 && (
        <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
          <h3 className="font-display text-base font-bold text-slate-900">Riwayat Test</h3>
          <ul className="mt-3 divide-y divide-purple-50">
            {[...data.testAttempts].reverse().map((a) => (
              <li key={a.id} className="flex items-center justify-between py-2.5 text-sm">
                <span className="capitalize text-slate-600">{a.category} test</span>
                <span className="font-bold text-[#5B21B6]">{a.score}/100</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Runner({ cat, onFinish, onExit }) {
  const questions = questionBank[cat];
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marks, setMarks] = useState({});
  const [time, setTime] = useState(cat === "english" ? 30 * 60 : 20 * 60);
  const q = questions[idx];

  const answersRef = useRef(answers);
  answersRef.current = answers;

  const submit = useCallback(() => {
    const r = scoreTest(questions, answersRef.current);
    onFinish({ id: "t_" + Date.now(), category: cat, score: r.score, correct: r.correct, total: r.total, skillProfile: r.skillProfile, weakest: r.weakest, date: new Date().toISOString() });
  }, [questions, onFinish, cat]);

  useEffect(() => {
    const t = setInterval(() => setTime((s) => (s <= 1 ? (clearInterval(t), submit(), 0) : s - 1)), 1000);
    return () => clearInterval(t);
  }, [submit]);

  const mm = String(Math.floor(time / 60)).padStart(2, "0");
  const ss = String(time % 60).padStart(2, "0");

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onExit} className="text-sm font-medium text-slate-500 hover:text-[#5B21B6]"><ChevronLeft className="mr-1 inline h-4 w-4" /> Keluar</button>
        <span className="flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-sm font-bold text-[#5B21B6]" data-testid="test-timer"><Clock className="h-4 w-4" /> {mm}:{ss}</span>
      </div>

      <div>
        <div className="flex items-center justify-between text-xs font-medium text-slate-500">
          <span>Soal {idx + 1} / {questions.length}</span>
          <span>{q.section}</span>
        </div>
        <ProgressBar value={((idx + 1) / questions.length) * 100} className="mt-2" />
      </div>

      <div className="rounded-2xl border border-purple-100 bg-white p-6 shadow-sm" data-testid="test-question">
        <div className="mb-2 flex items-center justify-between">
          <span className="rounded bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-[#6D28D9]">Scholavia Practice Question</span>
          <button onClick={() => setMarks({ ...marks, [q.id]: !marks[q.id] })} className={`text-sm ${marks[q.id] ? "text-[#7C3AED]" : "text-slate-300"}`}><Bookmark className="h-4 w-4" /></button>
        </div>
        <p className="font-display text-lg font-semibold leading-snug text-slate-900">{q.q}</p>
        <div className="mt-5 space-y-2.5">
          {q.options.map((opt, i) => (
            <button key={opt} onClick={() => setAnswers({ ...answers, [q.id]: i })} data-testid={`option-${i}`}
              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-all ${
                answers[q.id] === i ? "border-[#7C3AED] bg-purple-50 text-[#4C1D95]" : "border-purple-100 text-slate-600 hover:bg-purple-50/50"
              }`}>
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${answers[q.id] === i ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-slate-300 text-slate-400"}`}>{String.fromCharCode(65 + i)}</span>
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="rounded-full border-purple-200"><ChevronLeft className="mr-1 h-4 w-4" /> Sebelumnya</Button>
        {idx < questions.length - 1 ? (
          <Button onClick={() => setIdx(idx + 1)} data-testid="test-next" className="rounded-full bg-[#7C3AED] hover:bg-[#6D28D9]">Selanjutnya <ChevronRight className="ml-1 h-4 w-4" /></Button>
        ) : (
          <Button onClick={submit} data-testid="test-submit" className="rounded-full bg-emerald-600 hover:bg-emerald-700">Selesai & Lihat Hasil</Button>
        )}
      </div>
    </div>
  );
}

function Result({ attempt, onRetry, onBack }) {
  if (!attempt) return null;
  const target = targetScores[attempt.category] || 80;
  const gap = attempt.score - target;
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <SectionTitle title="Hasil Test" desc="Assess → Diagnose → Practice → Reassess → Update Readiness" />
      <div className="rounded-2xl border border-purple-100 bg-white p-6 text-center shadow-sm">
        <ProgressRing value={attempt.score} label={`${attempt.score}`} sublabel={`/ 100`} size={140} testid="result-score" />
        <div className="mt-4 flex justify-center gap-6 text-sm">
          <div className="flex items-center gap-1.5"><Target className="h-4 w-4 text-[#7C3AED]" /> Target: <b>{target}</b></div>
          <div className={`flex items-center gap-1.5 ${gap < 0 ? "text-red-500" : "text-emerald-600"}`}><TrendingDown className="h-4 w-4" /> Gap: <b>{gap > 0 ? `+${gap}` : gap}</b></div>
        </div>
      </div>

      <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
        <h3 className="font-display text-base font-bold text-slate-900">Skill Profile</h3>
        <div className="mt-3 space-y-3">
          {attempt.skillProfile.map((s) => (
            <div key={s.section}>
              <div className="flex justify-between text-sm"><span className="text-slate-600">{s.section}</span><span className="font-bold text-slate-800">{s.pct}%</span></div>
              <ProgressBar value={s.pct} className="mt-1" color={s.pct < 65 ? "#EF4444" : "#8B5CF6"} />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-purple-100 bg-[#FBFAFF] p-5">
        <h3 className="font-display text-base font-bold text-slate-900">Rekomendasi</h3>
        <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
          <li>• Skill terlemah: <b>{attempt.weakest.map((w) => w.section).join(", ")}</b>, fokus latihan di sini.</li>
          <li>• Latihan terarah lalu <b>reassess</b> untuk memperbarui readiness.</li>
          <li>• Test berikutnya yang disarankan: ulangi setelah 1-2 minggu latihan.</li>
        </ul>
      </div>

      <div className="flex gap-3">
        <Button onClick={onRetry} variant="outline" className="flex-1 rounded-full border-purple-200 text-[#5B21B6]"><RotateCcw className="mr-1.5 h-4 w-4" /> Ulangi Test</Button>
        <Link to="/app" className="flex-1"><Button className="w-full rounded-full bg-[#7C3AED] hover:bg-[#6D28D9]">Ke Dashboard <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
      </div>
    </div>
  );
}
